import { useEffect } from "react";
import { useHref, useLocation, useNavigate } from "react-router";
import {
  tutorialSidebarHref,
  tutorialSidebarRoute,
} from "./tutorialSidebarHref.js";

const sidebarLinks =
  'nav a[href*="/getting-started/"], [role="dialog"] a[href*="/getting-started/"]';

const isSidebarLink = (anchor: HTMLAnchorElement) => {
  if (anchor.closest("nav")?.closest('[data-pagefind-ignore="all"]')) {
    return true;
  }

  const dialog = anchor.closest('[role="dialog"]');
  const titleId = dialog?.getAttribute("aria-labelledby");
  return Boolean(
    titleId &&
    document.getElementById(titleId)?.textContent?.trim() === "Navigation",
  );
};

/** Keep Zudoku's step rows on the method selected in the current URL. */
export function TutorialSidebarLinks() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const routerRoot = useHref("/");

  useEffect(() => {
    const updateAnchor = (anchor: HTMLAnchorElement) => {
      if (!isSidebarLink(anchor)) return null;
      const href = anchor.getAttribute("href");
      if (!href) return null;
      const target = tutorialSidebarHref(pathname, href);
      if (target && href !== target) anchor.setAttribute("href", target);
      return target;
    };

    const updateLinks = () => {
      document
        .querySelectorAll<HTMLAnchorElement>(sidebarLinks)
        .forEach(updateAnchor);
    };

    const eventAnchor = (event: Event) => {
      const target = event.target;
      return target instanceof Element
        ? target.closest<HTMLAnchorElement>("a[href]")
        : null;
    };

    const updateEventAnchor = (event: Event) => {
      const anchor = eventAnchor(event);
      if (anchor) updateAnchor(anchor);
    };

    const onClick = (event: MouseEvent) => {
      const anchor = eventAnchor(event);
      if (!anchor || !isSidebarLink(anchor)) return;
      const target = updateAnchor(anchor);
      if (!target) return;

      // Modified and non-primary clicks use the real href for a new tab/window.
      if (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        anchor.target === "_blank" ||
        anchor.hasAttribute("download")
      ) {
        return;
      }

      // Zudoku's NavLink still holds its original `to` prop after an href edit.
      // Intercept before React Router navigates to that original path.
      const route = tutorialSidebarRoute(target, routerRoot);
      if (!route) return;
      event.preventDefault();
      event.stopPropagation();
      navigate(route);

      // Zudoku closes its mobile drawer in the NavLink click handler, which we
      // bypass above. Escape closes the dialog through its own public behavior.
      const dialog = anchor.closest('[role="dialog"]');
      if (dialog) {
        dialog.dispatchEvent(
          new KeyboardEvent("keydown", { key: "Escape", bubbles: true }),
        );
      }
    };

    let frame = 0;
    const observer = new MutationObserver(() => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        updateLinks();
      });
    });

    updateLinks();
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["href"],
    });
    document.addEventListener("pointerdown", updateEventAnchor, true);
    document.addEventListener("contextmenu", updateEventAnchor, true);
    document.addEventListener("auxclick", updateEventAnchor, true);
    document.addEventListener("click", onClick, true);

    return () => {
      observer.disconnect();
      if (frame) cancelAnimationFrame(frame);
      document.removeEventListener("pointerdown", updateEventAnchor, true);
      document.removeEventListener("contextmenu", updateEventAnchor, true);
      document.removeEventListener("auxclick", updateEventAnchor, true);
      document.removeEventListener("click", onClick, true);
    };
  }, [pathname, navigate, routerRoot]);

  return null;
}
